import { Badge, Box, Menu, Text } from '@mantine/core'
import { IconCheck, IconExternalLink, IconTrash } from '@tabler/icons-react'
import { PendingReviewInfo, getLinkNoteText, getReviewLink } from './eventReview'

interface PendingReviewCellProps {
	review: PendingReviewInfo | null
	// Whether users can see the event (it is not hidden).
	listed: boolean
	onApprove: () => void
	onDelete: () => void
}

/**
 * The Review column of the Events table. An event found on the web that waits for review
 * shows one "Pending" tag; pressing it opens the link to the event's own page, Approve and
 * Delete. When the finder could not check the link, one short line under the tag says so.
 * Every other event says in plain text where it stands, "Listed" or "Hidden", so the column
 * explains itself.
 */
function PendingReviewCell({ review, listed, onApprove, onDelete }: PendingReviewCellProps) {
	if (!review) {
		return (
			<Text size="sm" color="dimmed">
				{listed ? 'Listed' : 'Hidden'}
			</Text>
		)
	}

	const eventLink = getReviewLink(review)
	const linkNote = getLinkNoteText(review.linkNote)

	return (
		<Box>
			<Menu shadow="md" width={200} position="bottom-start" withinPortal>
				<Menu.Target>
					<Badge
						radius={6}
						color="orange"
						variant="filled"
						component="button"
						type="button"
						aria-label="Pending review"
						sx={{ cursor: 'pointer', border: 0 }}
					>
						Pending
					</Badge>
				</Menu.Target>
				<Menu.Dropdown>
					{eventLink && (
						<Menu.Item
							component="a"
							href={eventLink}
							target="_blank"
							rel="noreferrer noopener"
							icon={<IconExternalLink size={14} />}
						>
							Open event page
						</Menu.Item>
					)}
					<Menu.Item icon={<IconCheck size={14} />} onClick={onApprove}>
						Approve
					</Menu.Item>
					<Menu.Item color="red" icon={<IconTrash size={14} />} onClick={onDelete}>
						Delete
					</Menu.Item>
				</Menu.Dropdown>
			</Menu>
			{linkNote && (
				<Text size="xs" color="orange" mt={2} weight={600} title={linkNote.full} sx={{ whiteSpace: 'nowrap' }}>
					{linkNote.short}
				</Text>
			)}
		</Box>
	)
}

export default PendingReviewCell
